import React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { Chess } from 'chess.js';

const TopMovesPanel = ({ topMoves, onMoveClick, currentFen }) => {
    const uciToSan = (uciMove, fen) => {
        try {
            const chess = new Chess(fen);
            const from = uciMove.slice(0, 2);
            const to = uciMove.slice(2, 4);
            const promotion = uciMove.length > 4 ? uciMove.slice(4) : undefined;
            
            const move = chess.move({ from, to, promotion });
            return move ? move.san : uciMove;
        } catch (error) {
            console.error('Error in uciToSan:', error);
            return uciMove;
        }
    };

    const formatFullLine = (pvLine, startFen) => {
        if (!pvLine || pvLine.length === 0) {
            return '';
        }
        
        try {
            const chess = new Chess(startFen);
            let result = '';
            
            const movesToShow = pvLine.slice(0, 15);
            
            for (let i = 0; i < movesToShow.length; i++) {
                const uciMove = movesToShow[i];
                const from = uciMove.slice(0, 2);
                const to = uciMove.slice(2, 4);
                const promotion = uciMove.length > 4 ? uciMove.slice(4) : undefined;
                
                const moveNumber = chess.moveNumber();
                const isWhite = chess.turn() === 'w';
                
                if (isWhite) {
                    result += (i > 0 ? ' ' : '') + `${moveNumber}. `;
                } else if (i === 0) {
                    result += `${moveNumber}... `;
                } else {
                    result += ' ';
                }
                
                const move = chess.move({ from, to, promotion });
                if (!move) break;
                
                result += move.san;
            }
            
            return result;
        } catch (error) {
            console.error('Error formatting line:', error);
            return '';
        }
    };

    const formatEvaluation = (evaluation) => {
        if (evaluation.type === 'mate') {
            const mateValue = evaluation.value;
            return {
                text: mateValue > 0 ? `+M${Math.abs(mateValue)}` : `-M${Math.abs(mateValue)}`,
                color: mateValue > 0 ? '#4caf50' : '#f44336'
            };
        }
        
        const value = evaluation.value;
        const displayValue = Math.abs(value).toFixed(2);
        
        return {
            text: value > 0 ? `+${displayValue}` : value < 0 ? `-${displayValue}` : '0.00',
            color: Math.abs(value) < 0.3 ? '#757575' : value > 0 ? '#4caf50' : '#f44336'
        };
    };

    // Common box style for consistent height
    const rowStyle = {
        mb: 0.5,
        p: 0.75,
        height: '38px', // Fixed height
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
    };

    // Show placeholder if no moves yet
    const showPlaceholder = !topMoves || topMoves.length === 0;

    return (
        <Paper elevation={2} sx={{ p: 0.75, width: '100%' }}>
            {showPlaceholder ? (
                // Render skeleton loaders with exact same dimensions
                <>
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                ...rowStyle,
                                mb: index < 2 ? 0.5 : 0,
                                bgcolor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Skeleton 
                                variant="text" 
                                width={18} 
                                height={16}
                                sx={{ flexShrink: 0 }}
                            />
                            <Skeleton 
                                variant="rectangular" 
                                width={55} 
                                height={24} 
                                sx={{ borderRadius: 0.5, flexShrink: 0 }} 
                            />
                            <Skeleton 
                                variant="text" 
                                sx={{ flex: 1 }} 
                                height={16} 
                            />
                            <Skeleton 
                                variant="text" 
                                width={35} 
                                height={16}
                                sx={{ flexShrink: 0 }}
                            />
                        </Box>
                    ))}
                </>
            ) : (
                topMoves.map((moveInfo, index) => {
                    const fullLine = formatFullLine(moveInfo.evaluation.pvLine, currentFen);
                    const evalInfo = formatEvaluation(moveInfo.evaluation);
                    const isTopMove = index === 0;
                    
                    return (
                        <Box
                            key={`${moveInfo.move}-${index}`}
                            sx={{
                                ...rowStyle,
                                mb: index < topMoves.length - 1 ? 0.5 : 0,
                                bgcolor: isTopMove ? 'primary.light' : 'background.default',
                                cursor: 'pointer',
                                border: isTopMove ? '1.5px solid' : '1px solid',
                                borderColor: isTopMove ? 'primary.main' : 'divider',
                                '&:hover': {
                                    bgcolor: isTopMove ? 'primary.main' : 'action.hover',
                                    boxShadow: 1
                                },
                                transition: 'background-color 0.15s, box-shadow 0.15s'
                            }}
                            onClick={() => onMoveClick(moveInfo.move)}
                        >
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    fontWeight: 'bold', 
                                    minWidth: 18,
                                    fontSize: '0.7rem',
                                    color: isTopMove ? 'primary.dark' : 'text.secondary',
                                    flexShrink: 0
                                }}
                            >
                                #{index + 1}
                            </Typography>
                            
                            <Box
                                sx={{
                                    minWidth: 55,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    textAlign: 'center',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '24px'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        color: evalInfo.color,
                                        lineHeight: 1
                                    }}
                                >
                                    {evalInfo.text}
                                </Typography>
                            </Box>
                            
                            <Typography
                                variant="caption"
                                sx={{
                                    flex: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    color: 'text.primary',
                                    fontWeight: isTopMove ? 600 : 400,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1
                                }}
                            >
                                {fullLine}
                            </Typography>
                            
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'text.disabled', 
                                    fontSize: '0.65rem',
                                    minWidth: 35,
                                    textAlign: 'right',
                                    flexShrink: 0,
                                    lineHeight: 1
                                }}
                            >
                                d{moveInfo.evaluation.depth}
                            </Typography>
                        </Box>
                    );
                })
            )}
        </Paper>
    );
};

export default TopMovesPanel;